/* oxlint-disable next/no-img-element -- Local object-URL previews cannot use a framework image optimizer. */
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import {
  LIMITS,
  calculatePlacement,
  constrainedCanvasSize,
  formatBytes,
  megapixels,
  resolvePageSize,
  rotatedSize,
  sanitizePdfFilename,
  validateFilePolicy,
  type FitMode,
  type PageSize,
  type Quality,
} from './lib/pdf-utils';
import { loadIdeaIdentity, sendAppIdea } from './lib/idea-api';
import { trackAnalytics } from './lib/analytics';

type PhotoItem = {
  id: string;
  file: File;
  previewUrl: string;
  width: number;
  height: number;
  rotation: 0 | 90 | 180 | 270;
};

type Notice = {
  id: string;
  message: string;
};

type BuildResult = {
  blob: Blob;
  url: string;
  pages: number;
  skipped: number;
};

type ProgressState = {
  current: number;
  total: number;
  label: string;
};

type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

type NavigatorWithStandalone = Navigator & {
  standalone?: boolean;
};

function isRunningStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as NavigatorWithStandalone).standalone === true;
}

const makeId = () => crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function Icon({ children, size = 20 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

const icons = {
  plus: (
    <Icon><path d="M12 5v14M5 12h14" /></Icon>
  ),
  upload: (
    <Icon size={26}><path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" /><path d="M5 15v4h14v-4" /></Icon>
  ),
  photo: (
    <Icon size={25}><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="9" cy="9" r="1.5" /><path d="m5.5 17 4.2-4.2 3.1 3.1 2.1-2.1 3.6 3.2" /></Icon>
  ),
  up: (
    <Icon size={17}><path d="m7 14 5-5 5 5" /></Icon>
  ),
  down: (
    <Icon size={17}><path d="m7 10 5 5 5-5" /></Icon>
  ),
  rotate: (
    <Icon size={17}><path d="M4.9 8A8 8 0 1 1 4 13" /><path d="M4 4v4h4" /></Icon>
  ),
  trash: (
    <Icon size={17}><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></Icon>
  ),
  file: (
    <Icon size={22}><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v5h5M9.5 13h5M9.5 17h5" /></Icon>
  ),
  download: (
    <Icon><path d="M12 3v12m0 0 4-4m-4 4-4-4" /><path d="M5 19h14" /></Icon>
  ),
  share: (
    <Icon><circle cx="18" cy="5" r="2.5" /><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="19" r="2.5" /><path d="m8.2 10.8 7.6-4.5m-7.6 6.9 7.6 4.5" /></Icon>
  ),
  check: (
    <Icon size={18}><path d="m5 12 4 4L19 6" /></Icon>
  ),
  close: (
    <Icon size={16}><path d="m7 7 10 10M17 7 7 17" /></Icon>
  ),
  install: (
    <Icon size={17}><rect x="6" y="2.5" width="12" height="19" rx="3" /><path d="M12 6v8m0 0 3-3m-3 3-3-3M10 18h4" /></Icon>
  ),
};

async function decodePhoto(file: File): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if ('createImageBitmap' in window) {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        close: () => bitmap.close(),
      };
    } catch {
      // Some Safari versions reject imageOrientation even when ImageBitmap exists.
      try {
        const bitmap = await createImageBitmap(file);
        return {
          source: bitmap,
          width: bitmap.width,
          height: bitmap.height,
          close: () => bitmap.close(),
        };
      } catch {
        // Fall through to the broadly supported HTMLImageElement path.
      }
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      close: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: Quality): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('This browser could not encode the photo.'))),
      'image/jpeg',
      quality / 100,
    );
  });
}

async function rasterizePhoto(photo: PhotoItem, quality: Quality): Promise<{ bytes: ArrayBuffer; width: number; height: number }> {
  const decoded = await decodePhoto(photo.file);
  try {
    const oriented = rotatedSize(decoded.width, decoded.height, photo.rotation);
    const target = constrainedCanvasSize(oriented.width, oriented.height);
    const scale = Math.min(target.width / oriented.width, target.height / oriented.height);
    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('Canvas is unavailable in this browser.');

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.save();
    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate((photo.rotation * Math.PI) / 180);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(
      decoded.source,
      -(decoded.width * scale) / 2,
      -(decoded.height * scale) / 2,
      decoded.width * scale,
      decoded.height * scale,
    );
    context.restore();

    const jpeg = await canvasToJpeg(canvas, quality);
    return { bytes: await jpeg.arrayBuffer(), width: canvas.width, height: canvas.height };
  } finally {
    decoded.close();
  }
}

const yieldToBrowser = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

export function App() {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [pageSize, setPageSize] = useState<PageSize>('match');
  const [fitMode, setFitMode] = useState<FitMode>('contain');
  const [quality, setQuality] = useState<Quality>(84);
  const [filename, setFilename] = useState('my-photos');
  const [isDragging, setIsDragging] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addingLabel, setAddingLabel] = useState('');
  const [isBuilding, setIsBuilding] = useState(false);
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const [result, setResult] = useState<BuildResult | null>(null);
  const [resultMessage, setResultMessage] = useState('');
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(isRunningStandalone);
  const [installFeedback, setInstallFeedback] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [suggestionStatus, setSuggestionStatus] = useState('');
  const [ideaEmail, setIdeaEmail] = useState('');
  const [ideaEmailFromDropbox, setIdeaEmailFromDropbox] = useState(false);
  const [ideaSending, setIdeaSending] = useState(false);

  useEffect(() => {
    trackAnalytics('page_view', 'photos-to-pdf');
  }, []);

  useEffect(() => {
    void loadIdeaIdentity().then((identity) => {
      if (!identity.email) return;
      setIdeaEmail(identity.email);
      setIdeaEmailFromDropbox(true);
    }).catch(() => undefined);
  }, []);
  const inputRef = useRef<HTMLInputElement>(null);
  const installDialogRef = useRef<HTMLDialogElement>(null);
  const previewUrls = useRef(new Set<string>());
  const resultUrl = useRef<string | null>(null);
  const settingsRef = useRef({ pageSize, fitMode, quality, filename });
  const photoCountRef = useRef(photos.length);

  const totalBytes = useMemo(() => photos.reduce((total, photo) => total + photo.file.size, 0), [photos]);
  const busy = isAdding || isBuilding;
  const outputName = sanitizePdfFilename(filename);
  const shareSupported = typeof navigator !== 'undefined' && 'share' in navigator && 'canShare' in navigator;
  const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/i.test(userAgent)
    || (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(userAgent);
  const isSafari = /Safari/i.test(userAgent) && !/Chrome|Chromium|CriOS|Edg|OPR/i.test(userAgent);

  useEffect(() => {
    const urls = previewUrls.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      if (resultUrl.current) URL.revokeObjectURL(resultUrl.current);
    };
  }, []);

  useEffect(() => {
    settingsRef.current = { pageSize, fitMode, quality, filename };
    photoCountRef.current = photos.length;
  }, [filename, fitMode, pageSize, photos.length, quality]);

  useEffect(() => {
    const capturePrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const markInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
      setInstallFeedback('BuckSimple Photos to PDF is installed.');
    };
    window.addEventListener('beforeinstallprompt', capturePrompt);
    window.addEventListener('appinstalled', markInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', capturePrompt);
      window.removeEventListener('appinstalled', markInstalled);
    };
  }, []);

  useEffect(() => {
    const context = document.modelContext;
    if (!context?.registerTool) return;
    const lifecycle = new AbortController();

    void Promise.resolve(context.registerTool({
      name: 'configure_photos_pdf',
      title: 'Configure Photos to PDF',
      description: 'Stage page size, photo fit, image quality, or filename in the visible Photos to PDF workspace. Photos must still be selected by the person using the page.',
      inputSchema: {
        type: 'object',
        properties: {
          pageSize: { type: 'string', enum: ['match', 'letter', 'a4'] },
          fitMode: { type: 'string', enum: ['contain', 'fill'] },
          quality: { type: 'integer', enum: [72, 84, 92] },
          filename: { type: 'string', minLength: 1, maxLength: 100 },
        },
        minProperties: 1,
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, untrustedContentHint: false },
      async execute(input) {
        if (!isRecord(input)) throw new Error('Configuration must be an object.');
        const allowedKeys = new Set(['pageSize', 'fitMode', 'quality', 'filename']);
        if (!Object.keys(input).length || Object.keys(input).some((key) => !allowedKeys.has(key))) {
          throw new Error('Provide at least one supported PDF setting.');
        }
        if (input.pageSize !== undefined && (typeof input.pageSize !== 'string' || !['match', 'letter', 'a4'].includes(input.pageSize))) {
          throw new Error('pageSize must be match, letter, or a4.');
        }
        if (input.fitMode !== undefined && (typeof input.fitMode !== 'string' || !['contain', 'fill'].includes(input.fitMode))) {
          throw new Error('fitMode must be contain or fill.');
        }
        if (input.quality !== undefined && (typeof input.quality !== 'number' || ![72, 84, 92].includes(input.quality))) {
          throw new Error('quality must be 72, 84, or 92.');
        }
        if (input.filename !== undefined && (typeof input.filename !== 'string' || !input.filename.trim() || input.filename.length > 100)) {
          throw new Error('filename must be 1–100 characters.');
        }

        const current = settingsRef.current;
        const next = {
          pageSize: (input.pageSize ?? current.pageSize) as PageSize,
          fitMode: (input.fitMode ?? current.fitMode) as FitMode,
          quality: (input.quality ?? current.quality) as Quality,
          filename: typeof input.filename === 'string'
            ? input.filename.trim().replace(/\.pdf$/i, '')
            : current.filename,
        };
        if (resultUrl.current) URL.revokeObjectURL(resultUrl.current);
        resultUrl.current = null;
        setResult(null);
        setResultMessage('');
        setPageSize(next.pageSize);
        setFitMode(next.fitMode);
        setQuality(next.quality);
        setFilename(next.filename);
        settingsRef.current = next;
        await yieldToBrowser();
        return {
          configured: true,
          ...next,
          filename: sanitizePdfFilename(next.filename),
          photosReady: photoCountRef.current,
        };
      },
    }, { signal: lifecycle.signal })).catch(() => {
      // WebMCP is progressive enhancement; the visible workflow remains complete.
    });

    return () => lifecycle.abort();
  }, []);

  const clearResult = () => {
    if (resultUrl.current) URL.revokeObjectURL(resultUrl.current);
    resultUrl.current = null;
    setResult(null);
    setResultMessage('');
  };

  const addFiles = async (incoming: File[]) => {
    if (!incoming.length || busy) return;
    setIsAdding(true);
    setResultMessage('');
    const nextItems: PhotoItem[] = [];
    const nextNotices: Notice[] = [];
    let acceptedCount = photos.length;
    let acceptedBytes = totalBytes;

    for (let index = 0; index < incoming.length; index += 1) {
      const file = incoming[index];
      setAddingLabel(`Checking ${index + 1} of ${incoming.length}: ${file.name}`);
      const policyError = validateFilePolicy(file, acceptedCount, acceptedBytes);
      if (policyError) {
        nextNotices.push({ id: makeId(), message: policyError });
        continue;
      }

      try {
        const decoded = await decodePhoto(file);
        const { width, height } = decoded;
        decoded.close();
        if (!width || !height) throw new Error('The image has no readable dimensions.');
        if (megapixels(width, height) > LIMITS.maxMegapixels) {
          nextNotices.push({
            id: makeId(),
            message: `${file.name}: ${megapixels(width, height).toFixed(1)} megapixels is above the 50 MP limit.`,
          });
          continue;
        }
        const previewUrl = URL.createObjectURL(file);
        previewUrls.current.add(previewUrl);
        nextItems.push({ id: makeId(), file, previewUrl, width, height, rotation: 0 });
        acceptedCount += 1;
        acceptedBytes += file.size;
      } catch {
        nextNotices.push({ id: makeId(), message: `${file.name}: this photo could not be read.` });
      }
      await yieldToBrowser();
    }

    if (nextItems.length) {
      clearResult();
      setPhotos((current) => [...current, ...nextItems]);
    }
    setNotices((current) => [...current, ...nextNotices].slice(-8));
    setAddingLabel('');
    setIsAdding(false);
  };

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    void addFiles(files);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    void addFiles(Array.from(event.dataTransfer.files));
  };

  const movePhoto = (index: number, direction: -1 | 1) => {
    const destination = index + direction;
    if (destination < 0 || destination >= photos.length || busy) return;
    clearResult();
    setPhotos((current) => {
      const reordered = [...current];
      const [moving] = reordered.splice(index, 1);
      reordered.splice(destination, 0, moving);
      return reordered;
    });
  };

  const rotatePhoto = (id: string) => {
    if (busy) return;
    clearResult();
    setPhotos((current) => current.map((photo) => (
      photo.id === id
        ? { ...photo, rotation: ((photo.rotation + 90) % 360) as PhotoItem['rotation'] }
        : photo
    )));
  };

  const removePhoto = (id: string) => {
    if (busy) return;
    clearResult();
    setPhotos((current) => {
      const target = current.find((photo) => photo.id === id);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
        previewUrls.current.delete(target.previewUrl);
      }
      return current.filter((photo) => photo.id !== id);
    });
  };

  const clearAll = () => {
    if (busy) return;
    photos.forEach((photo) => {
      URL.revokeObjectURL(photo.previewUrl);
      previewUrls.current.delete(photo.previewUrl);
    });
    clearResult();
    setPhotos([]);
    setNotices([]);
  };

  const updatePageSize = (value: PageSize) => {
    if (value !== pageSize) clearResult();
    setPageSize(value);
  };

  const updateFitMode = (value: FitMode) => {
    if (value !== fitMode) clearResult();
    setFitMode(value);
  };

  const updateQuality = (value: Quality) => {
    if (value !== quality) clearResult();
    setQuality(value);
  };

  const buildPdf = async () => {
    if (!photos.length || busy) return;
    trackAnalytics('tool_attempt', 'photos-to-pdf');
    clearResult();
    setIsBuilding(true);
    setResultMessage('');
    setProgress({ current: 0, total: photos.length, label: 'Preparing PDF…' });
    const buildErrors: Notice[] = [];

    try {
      const { PDFDocument, rgb } = await import('pdf-lib');
      const document = await PDFDocument.create();
      document.setTitle(outputName.replace(/\.pdf$/i, ''));
      document.setAuthor('BuckSimple');
      document.setCreator('BuckSimple Photos to PDF');
      document.setProducer('pdf-lib');
      let pages = 0;

      for (let index = 0; index < photos.length; index += 1) {
        const photo = photos[index];
        setProgress({ current: index, total: photos.length, label: `Processing ${index + 1} of ${photos.length}: ${photo.file.name}` });
        await yieldToBrowser();
        try {
          const raster = await rasterizePhoto(photo, quality);
          const image = await document.embedJpg(raster.bytes);
          const pageDimensions = resolvePageSize(pageSize, raster.width, raster.height);
          const page = document.addPage([pageDimensions.width, pageDimensions.height]);
          page.drawRectangle({
            x: 0,
            y: 0,
            width: pageDimensions.width,
            height: pageDimensions.height,
            color: rgb(1, 1, 1),
          });
          const placement = calculatePlacement(
            { width: raster.width, height: raster.height },
            pageDimensions,
            fitMode,
          );
          page.drawImage(image, placement);
          pages += 1;
        } catch {
          buildErrors.push({ id: makeId(), message: `${photo.file.name}: skipped because it could not be processed.` });
        }
      }

      if (!pages) throw new Error('None of the photos could be processed. Try removing the damaged file and adding it again.');
      setProgress({ current: photos.length, total: photos.length, label: 'Finishing your PDF…' });
      await yieldToBrowser();
      const saved = await document.save({ useObjectStreams: true });
      const bytes = saved.buffer.slice(saved.byteOffset, saved.byteOffset + saved.byteLength) as ArrayBuffer;
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      resultUrl.current = url;
      setResult({ blob, url, pages, skipped: buildErrors.length });
      trackAnalytics('tool_success', 'photos-to-pdf');
      setNotices((current) => [...current, ...buildErrors].slice(-8));
      setResultMessage(buildErrors.length
        ? `PDF ready with ${pages} ${pages === 1 ? 'page' : 'pages'}. ${buildErrors.length} ${buildErrors.length === 1 ? 'photo was' : 'photos were'} skipped.`
        : `PDF ready with ${pages} ${pages === 1 ? 'page' : 'pages'}.`);
    } catch (error) {
      setResultMessage(error instanceof Error ? error.message : 'The PDF could not be created.');
    } finally {
      setProgress(null);
      setIsBuilding(false);
    }
  };

  const sharePdf = async () => {
    if (!result || !shareSupported) return;
    const file = new File([result.blob], outputName, { type: 'application/pdf' });
    try {
      if (!navigator.canShare({ files: [file] })) {
        setResultMessage('Sharing files is not supported here. Download the PDF instead.');
        return;
      }
      await navigator.share({ files: [file], title: outputName });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setResultMessage('The share sheet could not open. Your PDF is still ready to download.');
    }
  };

  const startInstall = async () => {
    if (isInstalled) return;
    setInstallFeedback('');
    if (installPrompt) {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      setInstallPrompt(null);
      setInstallFeedback(choice.outcome === 'accepted'
        ? 'Installation started. Your browser will finish adding the app.'
        : 'Installation was dismissed. You can try again from your browser menu.');
      return;
    }
    installDialogRef.current?.showModal();
  };

  const sendSuggestion = async () => {
    const idea = suggestion.trim();
    if (idea.length < 3) {
      setSuggestionStatus('Please add a few words about the app you want.');
      return;
    }
    if (!ideaEmail.trim()) {
      setSuggestionStatus('Enter your email so Rob can reply.');
      return;
    }
    setIdeaSending(true);
    setSuggestionStatus('Sending…');
    try {
      await sendAppIdea({ idea, replyEmail: ideaEmail });
      setSuggestion('');
      setSuggestionStatus('Thanks — Rob has your idea.');
    } catch (error) {
      setSuggestionStatus(error instanceof Error ? error.message : 'Your idea could not be sent. Please try again.');
    } finally {
      setIdeaSending(false);
    }
  };

  const installGuide = isIos
    ? {
        title: 'Add it to your iPhone',
        intro: 'Save this tool beside your other apps—no App Store download needed.',
        steps: ['Tap the Share button in your browser.', 'Scroll and choose Add to Home Screen.', 'Tap Add to place the icon on your Home Screen.'],
      }
    : isAndroid
      ? {
          title: 'Add it to your phone',
          intro: 'Your browser can save this site as an app-like shortcut.',
          steps: ['Open your browser menu (usually ⋮).', 'Choose Install app or Add to Home screen.', 'Confirm to place the icon on your Home Screen.'],
        }
      : isSafari
        ? {
            title: 'Add it to your Mac',
            intro: 'Safari can keep this tool in your Dock for quick access.',
            steps: ['Open the File menu in Safari.', 'Choose Add to Dock.', 'Confirm the name and click Add.'],
          }
        : {
            title: 'Install this BuckSimple tool',
            intro: 'If your browser supports site installation, you can open it like an app.',
            steps: ['Open your browser menu.', 'Choose Install BuckSimple Photos to PDF, Install app, or Create shortcut.', 'Confirm to add the icon to your device.'],
          };

  return (
    <div className="site-frame">
      <header className="site-header">
        <a className="brand" href="https://bucksimple.com" aria-label="BuckSimple home">
          <img className="brand-logo" src="/favicon.svg" alt="" width={40} height={40} />
          <span className="brand-wordmark">BuckSimple</span>
        </a>
        <div className="header-actions">
          <span className="product-name">Photos → PDF</span>
          <button className="install-button" type="button" onClick={() => void startInstall()} disabled={isInstalled}>
            {isInstalled ? icons.check : icons.install}
            <span>{isInstalled ? 'Installed' : installPrompt ? 'Install app' : 'Add to Home Screen'}</span>
          </button>
        </div>
        <output className="visually-hidden" aria-live="polite">{installFeedback}</output>
      </header>

      <main className="page-shell">
        <section className="intro" aria-labelledby="page-title">
          <div>
            <p className="eyebrow">One tidy document</p>
            <h1 id="page-title">JPG to PDF Converter</h1>
          </div>
          <p className="lede">BETA · Add JPG, PNG, or WebP photos, put them in order, and download one clean PDF. Everything happens on this device.</p>
        </section>

        <section className="workspace" aria-label="Photos to PDF maker">
          <div className="photo-panel">
            <div className="panel-heading">
              <div>
                <p className="step-kicker">01 · Photos</p>
                <h2>Build your page order</h2>
              </div>
              {photos.length > 0 && (
                <button className="text-button danger-text" type="button" onClick={clearAll} disabled={busy}>Clear all</button>
              )}
            </div>

            <div
              className={`drop-zone${isDragging ? ' is-dragging' : ''}`}
              onDragEnter={(event) => { event.preventDefault(); if (!busy) setIsDragging(true); }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setIsDragging(false); }}
              onDrop={onDrop}
            >
              <input
                ref={inputRef}
                className="visually-hidden"
                id="photo-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                multiple
                onChange={onInputChange}
                disabled={busy || photos.length >= LIMITS.maxFiles}
              />
              <div className="drop-icon">{icons.upload}</div>
              <div className="drop-copy">
                <strong>{photos.length ? 'Add more photos' : 'Drop photos here'}</strong>
                <span>{photos.length ? 'or choose more from your device' : 'or choose them from your device'}</span>
              </div>
              <button
                className="add-button"
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy || photos.length >= LIMITS.maxFiles}
              >
                {icons.plus}
                Choose photos
              </button>
            </div>

            {isAdding && (
              <output className="checking-row" aria-live="polite">
                <span className="spinner" aria-hidden="true" />
                <span>{addingLabel}</span>
              </output>
            )}

            {notices.length > 0 && (
              <div className="notice-stack" aria-label="Photo issues">
                {notices.map((notice) => (
                  <output className="notice" key={notice.id}>
                    <span>{notice.message}</span>
                    <button
                      type="button"
                      aria-label="Dismiss message"
                      onClick={() => setNotices((current) => current.filter((item) => item.id !== notice.id))}
                    >
                      {icons.close}
                    </button>
                  </output>
                ))}
              </div>
            )}

            {photos.length === 0 ? (
              <div className="empty-state">
                <span className="empty-number">0</span>
                <div>
                  <strong>Your PDF starts here.</strong>
                  <p>Add up to 25 photos. You can rotate them and change their order before making the PDF.</p>
                </div>
              </div>
            ) : (
              <ol className="photo-list" aria-label="PDF page order">
                {photos.map((photo, index) => {
                  const displaySize = rotatedSize(photo.width, photo.height, photo.rotation);
                  return (
                    <li className="photo-item" key={photo.id}>
                      <span className="page-number" aria-label={`Page ${index + 1}`}>{String(index + 1).padStart(2, '0')}</span>
                      <div className="thumbnail-wrap">
                        <img
                          src={photo.previewUrl}
                          alt=""
                          className="thumbnail"
                          style={{ transform: `rotate(${photo.rotation}deg)` }}
                        />
                      </div>
                      <div className="photo-details">
                        <strong title={photo.file.name}>{photo.file.name}</strong>
                        <span>{displaySize.width} × {displaySize.height} · {formatBytes(photo.file.size)}</span>
                      </div>
                      <div className="photo-actions" aria-label={`Actions for ${photo.file.name}`}>
                        <button type="button" onClick={() => movePhoto(index, -1)} disabled={busy || index === 0} aria-label="Move photo up">
                          {icons.up}
                        </button>
                        <button type="button" onClick={() => movePhoto(index, 1)} disabled={busy || index === photos.length - 1} aria-label="Move photo down">
                          {icons.down}
                        </button>
                        <button type="button" onClick={() => rotatePhoto(photo.id)} disabled={busy} aria-label="Rotate photo clockwise">
                          {icons.rotate}
                        </button>
                        <button className="remove-action" type="button" onClick={() => removePhoto(photo.id)} disabled={busy} aria-label="Remove photo">
                          {icons.trash}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}

            <div className="batch-stats" aria-live="polite">
              <span><strong>{photos.length}</strong> / {LIMITS.maxFiles} photos</span>
              <span><strong>{formatBytes(totalBytes)}</strong> / 100 MB</span>
            </div>
          </div>

          <aside className="settings-panel" aria-labelledby="settings-title">
            <div className="panel-heading compact-heading">
              <div>
                <p className="step-kicker">02 · PDF</p>
                <h2 id="settings-title">Set the finish</h2>
              </div>
              <span className="pdf-stamp" aria-hidden="true">PDF</span>
            </div>

            <fieldset className="control-group" disabled={busy}>
              <legend>Page size</legend>
              <div className="choice-grid three-choices">
                {([
                  ['match', 'Match', 'Photo shape'],
                  ['letter', 'Letter', 'US standard'],
                  ['a4', 'A4', 'International'],
                ] as const).map(([value, label, note]) => (
                  <label className={`choice-card${pageSize === value ? ' is-selected' : ''}`} key={value}>
                    <input type="radio" name="page-size" value={value} checked={pageSize === value} onChange={() => updatePageSize(value)} />
                    <strong>{label}</strong>
                    <span>{note}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="control-group" disabled={busy}>
              <legend>Photo fit</legend>
              <div className="choice-grid two-choices">
                {([
                  ['contain', 'Contain', 'Show the full photo'],
                  ['fill', 'Fill', 'Crop to fill the page'],
                ] as const).map(([value, label, note]) => (
                  <label className={`choice-card${fitMode === value ? ' is-selected' : ''}`} key={value}>
                    <input type="radio" name="fit-mode" value={value} checked={fitMode === value} onChange={() => updateFitMode(value)} />
                    <strong>{label}</strong>
                    <span>{note}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="control-group" disabled={busy}>
              <legend>Image quality</legend>
              <div className="quality-control">
                {([72, 84, 92] as Quality[]).map((value) => (
                  <label className={quality === value ? 'is-selected' : ''} key={value}>
                    <input type="radio" name="quality" checked={quality === value} onChange={() => updateQuality(value)} />
                    <strong>{value}%</strong>
                  </label>
                ))}
              </div>
              <p className="field-note">84% is a strong balance of clarity and file size.</p>
            </fieldset>

            <div className="control-group">
              <label className="field-label" htmlFor="filename">File name</label>
              <div className="filename-control">
                <input
                  id="filename"
                  value={filename}
                  onChange={(event) => setFilename(event.target.value)}
                  maxLength={100}
                  disabled={busy}
                  aria-describedby="filename-suffix"
                />
                <span id="filename-suffix">.pdf</span>
              </div>
            </div>

            {progress && (
              <output className="progress-card" aria-live="polite">
                <div className="progress-copy">
                  <strong>{progress.label}</strong>
                  <span>{progress.current} / {progress.total}</span>
                </div>
                <div className="progress-track" aria-hidden="true">
                  <span style={{ width: `${Math.max(3, (progress.current / progress.total) * 100)}%` }} />
                </div>
              </output>
            )}

            {!result ? (
              <button className="make-button" type="button" onClick={() => void buildPdf()} disabled={!photos.length || busy}>
                <span className="button-icon">{icons.file}</span>
                <span>
                  <strong>{isBuilding ? 'Making your PDF…' : 'Make PDF'}</strong>
                  <small>{photos.length ? `${photos.length} ${photos.length === 1 ? 'page' : 'pages'} in current order` : 'Add photos to begin'}</small>
                </span>
              </button>
            ) : (
              <div className="result-card">
                <div className="result-heading">
                  <span className="success-icon">{icons.check}</span>
                  <div>
                    <strong>{result.skipped ? 'PDF made with a note' : 'Your PDF is ready'}</strong>
                    <span>{result.pages} {result.pages === 1 ? 'page' : 'pages'} · {formatBytes(result.blob.size)}</span>
                  </div>
                </div>
                <a className="download-button" href={result.url} download={outputName}>
                  {icons.download}
                  Download PDF
                </a>
                <button className="share-button" type="button" onClick={() => void sharePdf()} disabled={!shareSupported}>
                  {icons.share}
                  {shareSupported ? 'Share PDF' : 'Sharing unavailable here'}
                </button>
                <button className="text-button remake-button" type="button" onClick={clearResult}>Change settings</button>
              </div>
            )}

            <p className={`result-message${result ? ' success-message' : ''}`} aria-live="polite">{resultMessage}</p>

            <div className="privacy-note">
              <span className="privacy-lock" aria-hidden="true">●</span>
              <p><strong>Private by design.</strong> Photos stay in your browser. Nothing is uploaded.</p>
            </div>
          </aside>
        </section>

        <section className="suggestion" aria-labelledby="suggestion-title">
          <div>
            <p className="step-kicker">Your turn</p>
            <h2 id="suggestion-title">Want another simple app?</h2>
            <p>Tell us one annoying little job BuckSimple should fix next.</p>
          </div>
          <form onSubmit={(event) => { event.preventDefault(); void sendSuggestion(); }} noValidate>
            <label className="suggestion-email" htmlFor="app-idea-email">Your email
              <input id="app-idea-email" type="email" maxLength={254} required readOnly={ideaEmailFromDropbox} value={ideaEmail} onChange={(event) => setIdeaEmail(event.target.value)} placeholder="you@example.com" autoComplete="email" />
              {ideaEmailFromDropbox ? <span>Email filled automatically</span> : null}
            </label>
            <label className="visually-hidden" htmlFor="app-idea">Your BuckSimple app idea</label>
            <div className="suggestion-control">
              <input
                id="app-idea"
                value={suggestion}
                onChange={(event) => {
                  setSuggestion(event.target.value);
                  setSuggestionStatus('');
                }}
                placeholder="Example: turn a voice note into a checklist"
                maxLength={160}
              />
              <button type="submit" disabled={ideaSending}>{ideaSending ? 'Sending…' : 'Send idea'}</button>
            </div>
            <output aria-live="polite">{suggestionStatus || 'Sent here in BuckSimple — no email app opens.'}</output>
          </form>
        </section>
      </main>

      <footer>
        <div>
          <a className="brand footer-brand" href="https://bucksimple.com" aria-label="BuckSimple home">
            <img className="brand-logo" src="/favicon.svg" alt="" width={40} height={40} />
            <span className="brand-wordmark">BuckSimple</span>
          </a>
          <p>One small job. One simple app.</p>
        </div>
        <p className="footer-note">Photos are processed locally and cleared when you close this page. <a href="https://bucksimple.com/privacy/">Privacy</a> · <a href="https://bucksimple.com/terms/">Terms</a></p>
      </footer>

      <dialog
        ref={installDialogRef}
        className="install-dialog"
        aria-labelledby="install-title"
        aria-describedby="install-description"
      >
        <div className="install-dialog-card">
          <button className="dialog-close" type="button" onClick={() => installDialogRef.current?.close()} aria-label="Close install help">
            {icons.close}
          </button>
          <img className="install-app-icon" src="/icon-192.png" alt="" />
          <p className="step-kicker">Keep it handy</p>
          <h2 id="install-title">{installGuide.title}</h2>
          <p id="install-description">{installGuide.intro}</p>
          <ol>
            {installGuide.steps.map((step, index) => (
              <li key={step}>
                <span>{index + 1}</span>
                <p>{step}</p>
              </li>
            ))}
          </ol>
          <p className="store-note">This installs the website shortcut from your browser. It is not an App Store or Play Store listing.</p>
          <form method="dialog">
            <button className="dialog-done" type="submit">Got it</button>
          </form>
        </div>
      </dialog>
    </div>
  );
}
